import { PopupConfirm, PopupSuccess } from "@components/general/popup";
import { PermitService } from "@services/permit.service";
import { useThemeStore } from "@stores/theme.store";
import { useMutation } from "@tanstack/react-query";
import { Button, message } from "antd";
import { NoticeType } from "antd/es/message/interface";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LuCalendar,
  LuCalendarCheck,
  LuClipboardList,
  LuFileText,
  LuUser,
} from "react-icons/lu";

moment.locale("id");

type InfoRowProps = { label: string; value: React.ReactNode };
const InfoRow = ({ label, value }: InfoRowProps) => (
  <div className="tw-flex tw-justify-between tw-items-start tw-py-2.5 tw-gap-3">
    <span className="tw-text-xs tw-text-text-secondary tw-shrink-0 tw-w-2/5">
      {label}
    </span>
    <span className="tw-text-sm tw-font-semibold tw-text-text-primary tw-text-right tw-flex-1">
      {value}
    </span>
  </div>
);

const Divider = () => (
  <div className="tw-h-px" style={{ background: "#f1f1f1" }} />
);

const statusConfig = (acc: string) => {
  if (acc === "Y")
    return {
      label: "Diizinkan",
      bg: "#f0fdf4",
      text: "#16a34a",
      ring: "#bbf7d0",
    };
  if (acc === "N")
    return {
      label: "Ditolak",
      bg: "#fef2f2",
      text: "#dc2626",
      ring: "#fecaca",
    };
  return {
    label: "Menunggu Konfirmasi",
    bg: "#fffbeb",
    text: "#d97706",
    ring: "#fde68a",
  };
};

export const PermitDetailPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { setHeader, setAppBar } = useThemeStore((s) => s);
  const [messageApi, contextHolder] = message.useMessage();
  const [permitID, setPermitID] = useState<string>("");
  const [popupTitle, setPopupTitle] = useState<string>("");
  const [popupMessage, setPopupMessage] = useState<string>("");
  const [popupTextConfirm, setPopupTextConfirm] = useState<string>("");
  const [popupCancel, setPopupCancel] = useState<boolean>(false);
  const [popupSuccess, setPopupSuccess] = useState<boolean>(false);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [showMessageText, setShowMessageText] = useState<string>("");
  const [messageType, setMessageType] = useState<string>("");

  const { mutate, isPending } = useMutation({
    mutationFn: PermitService.permitCancel,
    onSuccess: (response) => {
      if (response.success) {
        setPermitID("");
        setPopupCancel(false);
        setPopupSuccess(true);
      } else {
        setPopupCancel(false);
        setShowMessage(true);
        setMessageType("error");
        setShowMessageText(response.message);
      }
    },
    onError: (error: any) => {
      setShowMessage(true);
      setMessageType("error");
      setShowMessageText(error.message ?? "Internal Error");
    },
  });

  const onOpenPopupCancel = (id: string) => {
    setPermitID(id);
    setPopupTitle("Batalkan");
    setPopupMessage("Jika dibatalkan maka harus membuat ulang izin baru");
    setPopupTextConfirm("Ya, Batalkan");
    setPopupCancel(true);
  };

  const handleConfirm = (title: string) => {
    if (title === "Batalkan") {
      setPopupCancel(false);
      mutate({ id: permitID });
    }
  };

  useEffect(() => {
    setAppBar({ title: "Detail Perizinan", show: true, leading: "back" });
    setHeader({
      buttonBack: true,
      filter: false,
      title: "Detail Perizinan",
      image: false,
    });
  }, [setHeader, setAppBar]);

  useEffect(() => {
    if (showMessage) {
      messageApi.open({
        type: messageType as NoticeType,
        content: <>{showMessageText}</>,
      });
    }
    setShowMessage(false);
  }, [showMessage, showMessageText, messageType, messageApi]);

  const cfg = statusConfig(state?.acc ?? "");

  return (
    <>
      {contextHolder}
      <div className="tw-px-4 tw-pt-2 tw-pb-20 tw-space-y-3">
        {/* Hero card */}
        <div className="card-body tw-text-center tw-py-6">
          <div
            className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-3xl tw-w-16 tw-h-16 tw-mb-4"
            style={{ background: "#eef2ff" }}
          >
            <LuClipboardList className="tw-text-3xl tw-text-[#4f46e5]" />
          </div>

          <div className="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-text-xs tw-text-text-secondary tw-mb-3">
            <LuCalendar className="tw-text-sm" />
            {moment(state?.datePermit ?? Date.now()).format(
              "dddd, DD MMMM YYYY",
            )}
          </div>

          <div
            className="tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-semibold"
            style={{
              background: cfg.bg,
              color: cfg.text,
              border: `1.5px solid ${cfg.ring}`,
            }}
          >
            {cfg.label}
          </div>
        </div>

        {/* Detail card */}
        <div className="card-body">
          <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
            <div className="tw-w-1 tw-h-4 tw-rounded-full tw-bg-primary" />
            <span className="tw-text-xs tw-font-bold tw-text-text-secondary tw-uppercase tw-tracking-widest">
              Rincian Izin
            </span>
          </div>

          <InfoRow
            label="Tanggal Izin"
            value={
              <span className="tw-flex tw-items-center tw-justify-end tw-gap-1.5">
                <LuCalendar className="tw-text-text-secondary tw-shrink-0" />
                {moment(state?.datePermit ?? Date.now()).format("DD MMMM YYYY")}
              </span>
            }
          />

          {state?.dateBack && (
            <>
              <Divider />
              <InfoRow
                label="Tanggal Kembali"
                value={
                  <span className="tw-flex tw-items-center tw-justify-end tw-gap-1.5">
                    <LuCalendarCheck className="tw-text-text-secondary tw-shrink-0" />
                    {moment(state.dateBack).format("DD MMMM YYYY")}
                  </span>
                }
              />
            </>
          )}

          {state?.pickup && state.pickup !== "-" && (
            <>
              <Divider />
              <InfoRow
                label="Penjemput"
                value={
                  <span className="tw-flex tw-items-center tw-justify-end tw-gap-1.5">
                    <LuUser className="tw-text-text-secondary tw-shrink-0" />
                    {state.pickup}
                  </span>
                }
              />
            </>
          )}

          {state?.notes && state.notes !== "-" && (
            <>
              <Divider />
              <div className="tw-pt-2.5 tw-pb-1">
                <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1.5">
                  <LuFileText className="tw-text-sm tw-text-text-secondary" />
                  <span className="tw-text-xs tw-text-text-secondary">
                    Catatan
                  </span>
                </div>
                <p className="tw-text-sm tw-font-semibold tw-text-text-primary tw-leading-relaxed">
                  {state.notes}
                </p>
              </div>
            </>
          )}
        </div>

        {state?.status_izin === "MENUNGGU" && (
          <Button
            type="primary"
            htmlType="submit"
            loading={isPending}
            onClick={() => onOpenPopupCancel(state?.nourut ?? "")}
            className="tw-p-2 tw-h-10 tw-w-full tw-text-base tw-rounded-xl bg-gradient tw-text-background-200 focus:tw-shadow-none hover:tw-bg-background-200 hover:tw-text-accent-primary hover:tw-border-primary"
          >
            {isPending ? "" : "Batalkan Permintaan Izin"}
          </Button>
        )}
      </div>

      <PopupConfirm
        open={popupCancel}
        title={popupTitle}
        message={popupMessage}
        textConfirm={popupTextConfirm}
        textCancel="Kembali"
        onConfirm={() => handleConfirm(popupTitle)}
        onCancel={() => setPopupCancel(false)}
      />

      <PopupSuccess
        open={popupSuccess}
        title="Berhasil!"
        message="Permintaan Izin berhasil dibatalkan"
        textConfirm="Kembali"
        onConfirm={() => navigate(-1)}
      />
    </>
  );
};
